import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { SegmentedControl } from "panels/content/top-bar/TODO-put-in-package/SegmentedControl"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPanel } from "features/map/control/selectors"
import {
    setWeatherDetailedAreas,
    setWeatherFlashStrikes,
    setWeatherGroundStrikes,
    setWeatherParticleEffect,
} from "features/map/control/slice"
import { PrecipUnit, WeatherMode, WindUnit } from "features/map/control/types"
import { useWeatherPreferences } from "./useWeatherPreferences"

const WIND_UNITS = [
    WindUnit.Knots,
    WindUnit.MetersPerSecond,
    WindUnit.MilesPerHour,
    WindUnit.KilometersPerHour,
] as const

const PRECIP_UNITS = [PrecipUnit.Millimeters, PrecipUnit.Inches] as const

const CONFIGURABLE_MODES: ReadonlyArray<WeatherMode> = ["wind", "precipitation", "lightning"]

type Props = { isLight: boolean }

export const WeatherModeConfiguration = ({ isLight }: Props) => {
    const dispatch = useAppDispatch()
    const { mode, particleEffect, groundStrikes, flashStrikes, detailedAreas } =
        useAppSelector(selectWeatherPanel)
    const { windUnit, precipUnit, setWindUnit, setPrecipUnit } = useWeatherPreferences()

    const triggerColor = isLight ? "text-gray-500" : "text-gray-400"

    if (!CONFIGURABLE_MODES.includes(mode)) return null

    return (
        <Dropdown>
            <Dropdown.Trigger
                as="button"
                aria-label="Weather mode settings"
                className="group flex w-fit items-center gap-1 bg-transparent text-left"
            >
                <Icons.SmallIcon Icon={Icons.Gear} className={triggerColor} />
            </Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                position="top-center"
                className="w-[260px] rounded-xl bg-gray-900 p-4 shadow-lg"
            >
                <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-normal leading-[1.4] text-gray-0">Settings</span>
                    <Dropdown.Close as="button" aria-label="Close configuration menu">
                        <Icons.SmallIcon
                            Icon={Icons.XClose}
                            className="text-gray-100 hover:text-gray-0 cursor-pointer"
                        />
                    </Dropdown.Close>
                </div>
                {mode === "wind" && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-normal leading-[1.4] text-gray-0">
                                Animation
                            </span>
                            <ToggleSwitch
                                isToggled={particleEffect}
                                setIsToggled={(value) => dispatch(setWeatherParticleEffect(value))}
                            />
                        </div>
                        <SegmentedControl
                            segments={WIND_UNITS}
                            defaultSelectedIndex={WIND_UNITS.indexOf(windUnit)}
                            onChange={(index) => setWindUnit(WIND_UNITS[index])}
                        />
                    </div>
                )}
                {mode === "precipitation" && (
                    <SegmentedControl
                        segments={PRECIP_UNITS}
                        defaultSelectedIndex={PRECIP_UNITS.indexOf(precipUnit)}
                        onChange={(index) => setPrecipUnit(PRECIP_UNITS[index])}
                    />
                )}
                {mode === "lightning" && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-normal leading-[1.4] text-gray-0">
                                Ground strikes
                            </span>
                            <ToggleSwitch
                                isToggled={groundStrikes}
                                setIsToggled={(value) => dispatch(setWeatherGroundStrikes(value))}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-normal leading-[1.4] text-gray-0">
                                Potential strikes
                            </span>
                            <ToggleSwitch
                                isToggled={flashStrikes}
                                setIsToggled={(value) => dispatch(setWeatherFlashStrikes(value))}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-normal leading-[1.4] text-gray-0">
                                Strike area coverage
                            </span>
                            <ToggleSwitch
                                isToggled={detailedAreas}
                                setIsToggled={(value) => dispatch(setWeatherDetailedAreas(value))}
                            />
                        </div>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    )
}
